/**
 * NAUI Freediver O2 & CO2 Tolerance Tables.
 *
 * Transcribed from the NAUI Freediver Student Manual (2021), Appendix "Tables",
 * pages 60-61. Values are the manual's, unmodified.
 *
 * Structure, per the manual and confirmed against it row by row:
 *   O2  table - ventilation held CONSTANT at 2:00, hold time GROWS each set.
 *               Trains tolerance to hypoxia.
 *   CO2 table - hold time held CONSTANT, ventilation SHRINKS each set.
 *               Trains tolerance to the urge to breathe.
 *
 * Published sources and training apps are known to swap these two definitions.
 * The manual's own column layout settles it, and nauiTables.test.ts asserts the
 * structural invariants so a future edit cannot silently invert them.
 *
 * Two details that look like transcription errors but are in the source:
 *   - Sets 7 and 8 repeat the same value in every phase, both table types.
 *   - The manual prints the column totals with a trailing seconds field
 *     ("16:00:00" for 16 minutes). Stored here as seconds via manualTotals,
 *     which the tests use as a checksum against the summed set values.
 *
 * The O2 table's "Static%" column in the manual is relative to a 2:00
 * reference, NOT to the diver's personal max. Phase selection against a
 * measured max is handled in utils/apneaTables.ts, not here.
 */

export type ApneaTableType = 'o2' | 'co2';

export interface NauiTableSet {
  /** 1-8, as printed in the manual. */
  set: number;
  /** Ventilation ("Vent") period before this set's hold, in seconds. */
  ventSeconds: number;
  /** Breath-hold ("Static") duration, in seconds. */
  holdSeconds: number;
}

export interface NauiTable {
  sets: NauiTableSet[];
  /** Column totals as printed in the manual; used as a transcription checksum. */
  manualTotals: {
    ventSeconds: number;
    holdSeconds: number;
    combinedSeconds: number;
  };
}

export interface NauiPhase {
  /** Phase 1-8. Higher phases are longer holds, not a different protocol. */
  phase: number;
  o2: NauiTable;
  co2: NauiTable;
}

/** The reference max the manual's O2 "Static%" column is computed against. */
export const NAUI_O2_REFERENCE_SECONDS = 120;

export const NAUI_PHASES: NauiPhase[] = [
  {
    phase: 1,
    o2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 60 },
        { set: 2, ventSeconds: 120, holdSeconds: 75 },
        { set: 3, ventSeconds: 120, holdSeconds: 90 },
        { set: 4, ventSeconds: 120, holdSeconds: 105 },
        { set: 5, ventSeconds: 120, holdSeconds: 120 },
        { set: 6, ventSeconds: 120, holdSeconds: 135 },
        { set: 7, ventSeconds: 120, holdSeconds: 150 },
        { set: 8, ventSeconds: 120, holdSeconds: 150 },
      ],
      manualTotals: { ventSeconds: 960, holdSeconds: 885, combinedSeconds: 1845 },
    },
    co2: {
      sets: [
        { set: 1, ventSeconds: 150, holdSeconds: 90 },
        { set: 2, ventSeconds: 135, holdSeconds: 90 },
        { set: 3, ventSeconds: 120, holdSeconds: 90 },
        { set: 4, ventSeconds: 105, holdSeconds: 90 },
        { set: 5, ventSeconds: 90, holdSeconds: 90 },
        { set: 6, ventSeconds: 75, holdSeconds: 90 },
        { set: 7, ventSeconds: 60, holdSeconds: 90 },
        { set: 8, ventSeconds: 60, holdSeconds: 90 },
      ],
      manualTotals: { ventSeconds: 795, holdSeconds: 720, combinedSeconds: 1515 },
    },
  },
  {
    phase: 2,
    o2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 75 },
        { set: 2, ventSeconds: 120, holdSeconds: 90 },
        { set: 3, ventSeconds: 120, holdSeconds: 105 },
        { set: 4, ventSeconds: 120, holdSeconds: 120 },
        { set: 5, ventSeconds: 120, holdSeconds: 135 },
        { set: 6, ventSeconds: 120, holdSeconds: 150 },
        { set: 7, ventSeconds: 120, holdSeconds: 165 },
        { set: 8, ventSeconds: 120, holdSeconds: 165 },
      ],
      manualTotals: { ventSeconds: 960, holdSeconds: 1005, combinedSeconds: 1965 },
    },
    co2: {
      sets: [
        { set: 1, ventSeconds: 135, holdSeconds: 90 },
        { set: 2, ventSeconds: 120, holdSeconds: 90 },
        { set: 3, ventSeconds: 105, holdSeconds: 90 },
        { set: 4, ventSeconds: 90, holdSeconds: 90 },
        { set: 5, ventSeconds: 75, holdSeconds: 90 },
        { set: 6, ventSeconds: 60, holdSeconds: 90 },
        { set: 7, ventSeconds: 45, holdSeconds: 90 },
        { set: 8, ventSeconds: 45, holdSeconds: 90 },
      ],
      manualTotals: { ventSeconds: 675, holdSeconds: 720, combinedSeconds: 1395 },
    },
  },
  {
    phase: 3,
    o2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 90 },
        { set: 2, ventSeconds: 120, holdSeconds: 105 },
        { set: 3, ventSeconds: 120, holdSeconds: 120 },
        { set: 4, ventSeconds: 120, holdSeconds: 135 },
        { set: 5, ventSeconds: 120, holdSeconds: 150 },
        { set: 6, ventSeconds: 120, holdSeconds: 165 },
        { set: 7, ventSeconds: 120, holdSeconds: 180 },
        { set: 8, ventSeconds: 120, holdSeconds: 180 },
      ],
      manualTotals: { ventSeconds: 960, holdSeconds: 1125, combinedSeconds: 2085 },
    },
    co2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 90 },
        { set: 2, ventSeconds: 105, holdSeconds: 90 },
        { set: 3, ventSeconds: 90, holdSeconds: 90 },
        { set: 4, ventSeconds: 75, holdSeconds: 90 },
        { set: 5, ventSeconds: 60, holdSeconds: 90 },
        { set: 6, ventSeconds: 45, holdSeconds: 90 },
        { set: 7, ventSeconds: 30, holdSeconds: 90 },
        { set: 8, ventSeconds: 30, holdSeconds: 90 },
      ],
      manualTotals: { ventSeconds: 555, holdSeconds: 720, combinedSeconds: 1275 },
    },
  },
  {
    phase: 4,
    o2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 105 },
        { set: 2, ventSeconds: 120, holdSeconds: 120 },
        { set: 3, ventSeconds: 120, holdSeconds: 135 },
        { set: 4, ventSeconds: 120, holdSeconds: 150 },
        { set: 5, ventSeconds: 120, holdSeconds: 165 },
        { set: 6, ventSeconds: 120, holdSeconds: 180 },
        { set: 7, ventSeconds: 120, holdSeconds: 195 },
        { set: 8, ventSeconds: 120, holdSeconds: 195 },
      ],
      manualTotals: { ventSeconds: 960, holdSeconds: 1245, combinedSeconds: 2205 },
    },
    co2: {
      sets: [
        { set: 1, ventSeconds: 105, holdSeconds: 90 },
        { set: 2, ventSeconds: 90, holdSeconds: 90 },
        { set: 3, ventSeconds: 75, holdSeconds: 90 },
        { set: 4, ventSeconds: 60, holdSeconds: 90 },
        { set: 5, ventSeconds: 45, holdSeconds: 90 },
        { set: 6, ventSeconds: 30, holdSeconds: 90 },
        { set: 7, ventSeconds: 15, holdSeconds: 90 },
        { set: 8, ventSeconds: 15, holdSeconds: 90 },
      ],
      manualTotals: { ventSeconds: 435, holdSeconds: 720, combinedSeconds: 1155 },
    },
  },
  {
    phase: 5,
    o2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 120 },
        { set: 2, ventSeconds: 120, holdSeconds: 135 },
        { set: 3, ventSeconds: 120, holdSeconds: 150 },
        { set: 4, ventSeconds: 120, holdSeconds: 165 },
        { set: 5, ventSeconds: 120, holdSeconds: 180 },
        { set: 6, ventSeconds: 120, holdSeconds: 195 },
        { set: 7, ventSeconds: 120, holdSeconds: 210 },
        { set: 8, ventSeconds: 120, holdSeconds: 210 },
      ],
      manualTotals: { ventSeconds: 960, holdSeconds: 1365, combinedSeconds: 2325 },
    },
    co2: {
      sets: [
        { set: 1, ventSeconds: 105, holdSeconds: 105 },
        { set: 2, ventSeconds: 90, holdSeconds: 105 },
        { set: 3, ventSeconds: 75, holdSeconds: 105 },
        { set: 4, ventSeconds: 60, holdSeconds: 105 },
        { set: 5, ventSeconds: 45, holdSeconds: 105 },
        { set: 6, ventSeconds: 30, holdSeconds: 105 },
        { set: 7, ventSeconds: 15, holdSeconds: 105 },
        { set: 8, ventSeconds: 15, holdSeconds: 105 },
      ],
      manualTotals: { ventSeconds: 435, holdSeconds: 840, combinedSeconds: 1275 },
    },
  },
  {
    phase: 6,
    o2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 135 },
        { set: 2, ventSeconds: 120, holdSeconds: 150 },
        { set: 3, ventSeconds: 120, holdSeconds: 165 },
        { set: 4, ventSeconds: 120, holdSeconds: 180 },
        { set: 5, ventSeconds: 120, holdSeconds: 195 },
        { set: 6, ventSeconds: 120, holdSeconds: 210 },
        { set: 7, ventSeconds: 120, holdSeconds: 225 },
        { set: 8, ventSeconds: 120, holdSeconds: 225 },
      ],
      manualTotals: { ventSeconds: 960, holdSeconds: 1485, combinedSeconds: 2445 },
    },
    co2: {
      sets: [
        { set: 1, ventSeconds: 105, holdSeconds: 120 },
        { set: 2, ventSeconds: 90, holdSeconds: 120 },
        { set: 3, ventSeconds: 75, holdSeconds: 120 },
        { set: 4, ventSeconds: 60, holdSeconds: 120 },
        { set: 5, ventSeconds: 45, holdSeconds: 120 },
        { set: 6, ventSeconds: 30, holdSeconds: 120 },
        { set: 7, ventSeconds: 15, holdSeconds: 120 },
        { set: 8, ventSeconds: 15, holdSeconds: 120 },
      ],
      manualTotals: { ventSeconds: 435, holdSeconds: 960, combinedSeconds: 1395 },
    },
  },
  {
    phase: 7,
    o2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 150 },
        { set: 2, ventSeconds: 120, holdSeconds: 165 },
        { set: 3, ventSeconds: 120, holdSeconds: 180 },
        { set: 4, ventSeconds: 120, holdSeconds: 195 },
        { set: 5, ventSeconds: 120, holdSeconds: 210 },
        { set: 6, ventSeconds: 120, holdSeconds: 225 },
        { set: 7, ventSeconds: 120, holdSeconds: 240 },
        { set: 8, ventSeconds: 120, holdSeconds: 240 },
      ],
      manualTotals: { ventSeconds: 960, holdSeconds: 1605, combinedSeconds: 2565 },
    },
    co2: {
      sets: [
        { set: 1, ventSeconds: 105, holdSeconds: 135 },
        { set: 2, ventSeconds: 90, holdSeconds: 135 },
        { set: 3, ventSeconds: 75, holdSeconds: 135 },
        { set: 4, ventSeconds: 60, holdSeconds: 135 },
        { set: 5, ventSeconds: 45, holdSeconds: 135 },
        { set: 6, ventSeconds: 30, holdSeconds: 135 },
        { set: 7, ventSeconds: 15, holdSeconds: 135 },
        { set: 8, ventSeconds: 15, holdSeconds: 135 },
      ],
      manualTotals: { ventSeconds: 435, holdSeconds: 1080, combinedSeconds: 1515 },
    },
  },
  {
    phase: 8,
    o2: {
      sets: [
        { set: 1, ventSeconds: 120, holdSeconds: 165 },
        { set: 2, ventSeconds: 120, holdSeconds: 180 },
        { set: 3, ventSeconds: 120, holdSeconds: 195 },
        { set: 4, ventSeconds: 120, holdSeconds: 210 },
        { set: 5, ventSeconds: 120, holdSeconds: 225 },
        { set: 6, ventSeconds: 120, holdSeconds: 240 },
        { set: 7, ventSeconds: 120, holdSeconds: 255 },
        { set: 8, ventSeconds: 120, holdSeconds: 255 },
      ],
      manualTotals: { ventSeconds: 960, holdSeconds: 1725, combinedSeconds: 2685 },
    },
    co2: {
      sets: [
        { set: 1, ventSeconds: 105, holdSeconds: 150 },
        { set: 2, ventSeconds: 90, holdSeconds: 150 },
        { set: 3, ventSeconds: 75, holdSeconds: 150 },
        { set: 4, ventSeconds: 60, holdSeconds: 150 },
        { set: 5, ventSeconds: 45, holdSeconds: 150 },
        { set: 6, ventSeconds: 30, holdSeconds: 150 },
        { set: 7, ventSeconds: 15, holdSeconds: 150 },
        { set: 8, ventSeconds: 15, holdSeconds: 150 },
      ],
      manualTotals: { ventSeconds: 435, holdSeconds: 1200, combinedSeconds: 1635 },
    },
  },
];

export const NAUI_PHASE_NUMBERS = NAUI_PHASES.map(p => p.phase);

export const getNauiPhase = (phase: number): NauiPhase | undefined =>
  NAUI_PHASES.find(p => p.phase === phase);

export const getNauiTable = (phase: number, type: ApneaTableType): NauiTable | undefined =>
  getNauiPhase(phase)?.[type];

/** Longest hold in a phase's table - the value phase gating is checked against. */
export const maxHoldSeconds = (phase: number, type: ApneaTableType): number => {
  const table = getNauiTable(phase, type);
  if (!table) return 0;
  return Math.max(...table.sets.map(s => s.holdSeconds));
};

/** Total wall-clock duration of a table, so the UI can warn before a 40-minute session. */
export const tableDurationSeconds = (phase: number, type: ApneaTableType): number => {
  const table = getNauiTable(phase, type);
  if (!table) return 0;
  return table.sets.reduce((sum, s) => sum + s.ventSeconds + s.holdSeconds, 0);
};
